import { resolveXmaxReportEnvironment, XMAX_SDK_BUILD_ENV, XMAX_SDK_BUILD_REGION, type XmaxSdkRegion } from '../build-config'
import type { XmaxReportSource, XmaxTelemetryEvent, XmaxTelemetryParams } from './collector'

/** Domestic and overseas ingestion routes baked into each regional SDK build. */
export const XMAX_TELEMETRY_ENDPOINTS: Record<XmaxSdkRegion, string> = {
  cn: 'https://log.xmaxai.com/v1/probe/report',
  global: 'https://api.xmax.cloud/v1/probe/report',
}

export const XMAX_TELEMETRY_DEFAULT_ENDPOINT = XMAX_TELEMETRY_ENDPOINTS[XMAX_SDK_BUILD_REGION]

export function isTelemetryEndpointConfigured(endpoint: string | undefined): endpoint is string {
  if (!endpoint || endpoint.includes('__pending_sdk_telemetry_endpoint__')) return false
  try { return new URL(endpoint).protocol === 'https:' } catch { return false }
}

export type XmaxTelemetryNetworkMeta = {
  api_supported: boolean
  online?: boolean
  effective_type?: string
  connection_type?: string
  rtt_ms?: number
  downlink_mbps?: number
  save_data?: boolean
}

export type XmaxTelemetryDeviceMeta = {
  language?: string
  hardware_concurrency?: number
  device_memory_gb?: number
  max_touch_points?: number
}

export type XmaxTelemetryProbeMeta = {
  sent: number
  dropped: number
  retried: number
  exempted: number
}

export type XmaxTelemetryMetricSummary = {
  avg?: number
  min?: number
  max?: number
  p50: number | null
  p90: number | null
  sample_count: number
}

export type XmaxTelemetryRtcStatsBlock = {
  duration_ms?: number
  sample_count: number
  metrics: Record<string, XmaxTelemetryMetricSummary>
}

export type XmaxTelemetryRtcStats = {
  snapshot_reason?: string
  current_stage?: string
  current_stage_elapsed_ms?: number
  stats_started_offset_ms?: number
  stats_last_offset_ms?: number
  up_stats_age_ms?: number
  down_stats_age_ms?: number
  sampling_source?: string
  down_stall_count?: number
  down_stall_duration_ms?: number
  window: XmaxTelemetryRtcStatsBlock
  cumulative: XmaxTelemetryRtcStatsBlock
}

export type XmaxTelemetryMeta = {
  trace_id: string
  session_id: string | null
  generation_id?: string
  page_id: string
  sdk_version: string
  env: string
  report_source: XmaxReportSource
  ua: string
  t0: number
  ts: number
  seq: number
  probe: XmaxTelemetryProbeMeta
  network: XmaxTelemetryNetworkMeta
  device: XmaxTelemetryDeviceMeta
  api_key_md5?: string
  model?: string
  auto_start?: boolean
  stream?: { width?: number; height?: number; fps?: number }
  [key: string]: unknown
}

export type XmaxTelemetryWireEvent = {
  event: string
  offset: number
  duration?: number
  attrs?: XmaxTelemetryParams
}

/** One document-compatible envelope. */
export type XmaxTelemetryEnvelope = {
  meta: XmaxTelemetryMeta
  rtc_stats: XmaxTelemetryRtcStats | null
  events: XmaxTelemetryWireEvent[]
}

export type XmaxTelemetryPayload = XmaxTelemetryEnvelope

type BrowserConnection = {
  effectiveType?: unknown
  type?: unknown
  rtt?: unknown
  downlink?: unknown
  saveData?: unknown
}

const META_KEYS = new Set([
  'schema_version', 'xmax_sdk_version', 'trace_id', 'call_id',
  'trace_start_time_ms', 'page_id', 'session_id', 'generation_id', 'model', 'media_kind',
  'media_bytes', 'api_host', 'api_key_md5', 'env', 'auto_start', 'entry_point', 'stream_width',
  'report_source',
  'stream_height', 'stream_fps', 'rtc_room_id', 'rtc_sdk_version', 'visibility', 'online',
  'background_seen', 'asset_operation_id', 'asset_age_ms', 'operation_id',
])

const EVENT_NAMES: Record<XmaxTelemetryEvent['name'], string> = {
  sdk_api_request: 'api.request',
  sdk_capture_get_user_media_start: 'capture.get_user_media.start',
  sdk_capture_get_user_media_end: 'capture.get_user_media.end',
  sdk_asset_sts_request: 'asset.sts.request',
  sdk_asset_sts_response: 'asset.sts.response',
  sdk_asset_upload_start: 'asset.upload.start',
  sdk_asset_upload_end: 'asset.upload.end',
  sdk_api_session_request: 'api.session.request',
  sdk_api_session_response: 'api.session.response',
  sdk_rtc_join_request: 'rtc.signal.join.request',
  sdk_rtc_join_response: 'rtc.signal.join.response',
  sdk_rtc_publish_local_track: 'rtc.publish.local_track',
  sdk_generation_request_sent: 'generation.request.sent',
  sdk_generation_ack_received: 'generation.ack.received',
  sdk_rtc_subscribe_remote_track: 'rtc.subscribe.remote_track',
  sdk_render_output_first_frame: 'render.output_first_frame',
  sdk_context_set_request: 'context.set.request',
  sdk_context_set_response: 'context.set.response',
  sdk_session_connect: 'session.connect',
  sdk_session_state_change: 'session.state_change',
  sdk_session_error: 'session.error',
  sdk_session_disconnect: 'session.disconnect',
  sdk_rtc_stats_snapshot: 'rtc.stats.snapshot',
  sdk_rtc_remote_state_change: 'rtc.remote.state_change',
  sdk_rtc_connection_state_change: 'rtc.connection.state_change',
  sdk_rtc_token_state_change: 'rtc.token.state_change',
  sdk_rtc_teardown: 'rtc.teardown',
  sdk_probe_degraded: 'probe.degraded',
  sdk_probe_health: 'probe.health',
  sdk_probe_buffer_overflow: 'probe.buffer_overflow',
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

/** Browser-provided connection hints only. Geo/country enrichment belongs to ingestion. */
export function readTelemetryNetworkMeta(): XmaxTelemetryNetworkMeta {
  try {
    if (typeof navigator === 'undefined') return { api_supported: false }
    const connection = (navigator as Navigator & { connection?: BrowserConnection }).connection
    return {
      api_supported: Boolean(connection),
      online: typeof navigator.onLine === 'boolean' ? navigator.onLine : undefined,
      effective_type: typeof connection?.effectiveType === 'string' ? connection.effectiveType.slice(0, 32) : undefined,
      connection_type: typeof connection?.type === 'string' ? connection.type.slice(0, 32) : undefined,
      rtt_ms: finiteNumber(connection?.rtt),
      downlink_mbps: finiteNumber(connection?.downlink),
      save_data: typeof connection?.saveData === 'boolean' ? connection.saveData : undefined,
    }
  } catch { return { api_supported: false } }
}

export function readTelemetryDeviceMeta(): XmaxTelemetryDeviceMeta {
  try {
    if (typeof navigator === 'undefined') return {}
    const value = navigator as Navigator & { deviceMemory?: unknown }
    return {
      language: typeof navigator.language === 'string' ? navigator.language.slice(0, 32) : undefined,
      hardware_concurrency: finiteNumber(navigator.hardwareConcurrency),
      device_memory_gb: finiteNumber(value.deviceMemory),
      max_touch_points: finiteNumber(navigator.maxTouchPoints),
    }
  } catch { return {} }
}

export function telemetryEnvelopeKey(event: XmaxTelemetryEvent): string {
  const { trace_id: traceId, operation_id: operationId, page_id: pageId } = event.params
  if (typeof traceId === 'string') return `trace:${traceId}`
  if (typeof operationId === 'string') return `operation:${operationId}`
  return `page:${String(pageId ?? '')}`
}

function metricSummary(params: XmaxTelemetryParams, scope: 'window' | 'cumulative', metric: string): XmaxTelemetryMetricSummary | undefined {
  const prefix = `${scope}_${metric}_`
  const sampleCount = finiteNumber(params[`${prefix}sample_count`])
  if (sampleCount === undefined) return undefined
  return {
    sample_count: sampleCount,
    avg: finiteNumber(params[`${prefix}avg`]),
    min: finiteNumber(params[`${prefix}min`]),
    max: finiteNumber(params[`${prefix}max`]),
    p50: finiteNumber(params[`${prefix}p50`]) ?? null,
    p90: finiteNumber(params[`${prefix}p90`]) ?? null,
  }
}

function parseRtcStats(params: XmaxTelemetryParams): XmaxTelemetryRtcStats {
  const metricNames = new Set<string>()
  Object.keys(params).forEach(key => {
    const match = /^(?:window|cumulative)_(.+)_sample_count$/.exec(key)
    if (match) metricNames.add(match[1])
  })
  const windowMetrics: Record<string, XmaxTelemetryMetricSummary> = {}
  const cumulativeMetrics: Record<string, XmaxTelemetryMetricSummary> = {}
  metricNames.forEach(metric => {
    const window = metricSummary(params, 'window', metric)
    const cumulative = metricSummary(params, 'cumulative', metric)
    if (window) windowMetrics[metric] = window
    if (cumulative) cumulativeMetrics[metric] = cumulative
  })
  const addAlias = (target: Record<string, XmaxTelemetryMetricSummary>, alias: string, candidates: string[]) => {
    const found = candidates.map(name => target[name]).find(Boolean)
    if (found && !target[alias]) target[alias] = found
  }
  for (const target of [windowMetrics, cumulativeMetrics]) {
    addAlias(target, 'rtt_ms', ['down_rtt_ms', 'up_rtt_ms'])
    addAlias(target, 'packets_lost_rate', ['down_loss_rate', 'up_loss_rate'])
    addAlias(target, 'bitrate_up_kbps', ['up_bitrate_kbps'])
    addAlias(target, 'bitrate_down_kbps', ['down_bitrate_kbps'])
    addAlias(target, 'fps', ['down_decode_fps'])
  }
  const maxCount = (metrics: Record<string, XmaxTelemetryMetricSummary>) =>
    Math.max(0, ...Object.values(metrics).map(metric => metric.sample_count))
  return {
    snapshot_reason: typeof params.snapshot_reason === 'string' ? params.snapshot_reason : undefined,
    current_stage: typeof params.current_stage === 'string' ? params.current_stage : undefined,
    current_stage_elapsed_ms: finiteNumber(params.current_stage_elapsed_ms),
    stats_started_offset_ms: finiteNumber(params.stats_started_offset_ms),
    stats_last_offset_ms: finiteNumber(params.stats_last_offset_ms),
    up_stats_age_ms: finiteNumber(params.up_stats_age_ms),
    down_stats_age_ms: finiteNumber(params.down_stats_age_ms),
    sampling_source: typeof params.sampling_source === 'string' ? params.sampling_source : undefined,
    down_stall_count: finiteNumber(params.down_stall_count),
    down_stall_duration_ms: finiteNumber(params.down_stall_duration_ms),
    window: { duration_ms: finiteNumber(params.window_duration_ms), sample_count: maxCount(windowMetrics), metrics: windowMetrics },
    cumulative: { sample_count: maxCount(cumulativeMetrics), metrics: cumulativeMetrics },
  }
}

function normalizeAttrs(event: XmaxTelemetryEvent): XmaxTelemetryParams {
  const attrs: XmaxTelemetryParams = {}
  Object.keys(event.params).forEach(key => {
    if (['region', 'sdk_region', 'region_form'].includes(key)) return
    if (META_KEYS.has(key) || ['event_id', 'event_time_ms', 'event_seq', 'offset_ms', 'duration_ms'].includes(key)) return
    if (/^(window|cumulative)_.+|^stats_(started|last)_offset_ms$|^snapshot_reason$|^down_stall_/.test(key)) return
    attrs[key] = event.params[key]
  })
  if (typeof attrs.error_code === 'string' && attrs.code === undefined) attrs.code = attrs.error_code
  if (attrs.code === undefined && attrs.business_code !== undefined) attrs.code = attrs.business_code
  if (attrs.http_status !== undefined && attrs.status === undefined) attrs.status = attrs.http_status
  if (attrs.track_kind !== undefined && attrs.kind === undefined) attrs.kind = attrs.track_kind
  if (event.name === 'sdk_capture_get_user_media_end' && attrs.code === undefined && attrs.error_name !== undefined) attrs.code = attrs.error_name
  if (event.name === 'sdk_session_disconnect' && attrs.reason === undefined && attrs.failure_reason !== undefined) attrs.reason = attrs.failure_reason
  if (event.name === 'sdk_capture_get_user_media_start') {
    if (attrs.requested_width !== undefined) attrs.width = attrs.requested_width
    if (attrs.requested_height !== undefined) attrs.height = attrs.requested_height
    if (attrs.requested_fps !== undefined) attrs.fps = attrs.requested_fps
  }
  return attrs
}

export function createTelemetryPayload(
  batchId: string,
  events: readonly XmaxTelemetryEvent[],
  seq = 1,
  probe: XmaxTelemetryProbeMeta = { sent: 1, dropped: 0, retried: 0, exempted: 0 },
): XmaxTelemetryPayload {
  const first = events[0]
  const params = first?.params ?? {}
  const latest = events.reduce<XmaxTelemetryParams>((result, event) => ({ ...result, ...event.params }), {})
  const generationId = typeof params.generation_id === 'string'
    && params.generation_id.length > 0
    && events.every(event => event.params.generation_id === params.generation_id)
    ? params.generation_id : undefined
  const eventTime = finiteNumber(params.event_time_ms) ?? Date.now()
  const offset = finiteNumber(params.offset_ms) ?? 0
  const traceId = typeof latest.trace_id === 'string'
    ? latest.trace_id
    : typeof latest.operation_id === 'string'
      ? latest.operation_id
      : String(latest.page_id ?? batchId)
  const stream = {
    width: finiteNumber(latest.stream_width),
    height: finiteNumber(latest.stream_height),
    fps: finiteNumber(latest.stream_fps),
  }
  const reportSource = latest.report_source === 'open_platform' ? 'open_platform' : 'external_developer'
  const meta: XmaxTelemetryMeta = {
    trace_id: traceId,
    session_id: typeof latest.session_id === 'string' ? latest.session_id : null,
    generation_id: generationId,
    page_id: String(latest.page_id ?? ''),
    api_key_md5: typeof latest.api_key_md5 === 'string' ? latest.api_key_md5 : undefined,
    sdk_version: String(latest.xmax_sdk_version ?? ''),
    env: resolveXmaxReportEnvironment(reportSource, XMAX_SDK_BUILD_ENV),
    report_source: reportSource,
    ua: typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string' ? navigator.userAgent : '',
    model: typeof latest.model === 'string' ? latest.model : undefined,
    stream: Object.values(stream).some(value => value !== undefined) ? stream : undefined,
    t0: finiteNumber(latest.trace_start_time_ms) ?? Math.round(eventTime - offset),
    ts: Date.now(), seq, probe,
    network: readTelemetryNetworkMeta(),
    device: readTelemetryDeviceMeta(),
  }
  META_KEYS.forEach(key => {
    if (['xmax_sdk_version', 'trace_start_time_ms', 'stream_width', 'stream_height', 'stream_fps', 'online', 'generation_id'].includes(key)) return
    const value = latest[key]
    if (value !== undefined && value !== null && meta[key] === undefined) meta[key] = value
  })
  const statsEvent = [...events].reverse().find(event => event.name === 'sdk_rtc_stats_snapshot')
  const wireEvents = events
    .filter(event => event.name !== 'sdk_rtc_stats_snapshot')
    .map(event => {
      const attrs = normalizeAttrs(event)
      const duration = finiteNumber(event.params.duration_ms)
      return {
        event: EVENT_NAMES[event.name],
        offset: finiteNumber(event.params.offset_ms) ?? 0,
        duration,
        attrs: Object.keys(attrs).length ? attrs : undefined,
      }
    })
  return { meta, rtc_stats: statsEvent ? parseRtcStats(statsEvent.params) : null, events: wireEvents }
}

export class TelemetryTransportError extends Error {
  constructor(readonly retryable: boolean, readonly retryAfterMs?: number) {
    super('Telemetry ingestion failed')
    this.name = 'TelemetryTransportError'
  }
}

function parseRetryAfter(value: string | null): number | undefined {
  if (!value) return undefined
  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds >= 0) return Math.min(60_000, seconds * 1000)
  const at = Date.parse(value)
  return Number.isFinite(at) ? Math.max(0, Math.min(60_000, at - Date.now())) : undefined
}

function safeEndpoint(endpoint: string): URL {
  if (!isTelemetryEndpointConfigured(endpoint)) throw new TelemetryTransportError(false)
  return new URL(endpoint)
}

export async function sendTelemetryPayload(endpoint: string, body: string, timeoutMs: number): Promise<void> {
  const target = safeEndpoint(endpoint)
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(target.href, {
      method: 'POST', body,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit', keepalive: true, signal: controller.signal,
    })
    if (!response.ok) {
      const retryable = [408, 425, 429].includes(response.status) || response.status >= 500
      throw new TelemetryTransportError(retryable, parseRetryAfter(response.headers.get('Retry-After')))
    }
  } catch (error) {
    if (error instanceof TelemetryTransportError) throw error
    throw new TelemetryTransportError(true)
  } finally { clearTimeout(timeout) }
}

export function sendTelemetryBeacon(endpoint: string, body: string, maxBytes: number): boolean {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false
    const target = safeEndpoint(endpoint)
    const payload = new Blob([body], { type: 'text/plain;charset=UTF-8' })
    if (payload.size > maxBytes) return false
    return navigator.sendBeacon(target.href, payload)
  } catch { return false }
}
